import {Statistics, statisticsType, StatisticsType, StatisticsWrapper} from "./statistics";
import {transportCategory, TransportCategory} from "../transport-category";
import {calculateAverage} from "../../util/math-util";
import {Exclude, Expose} from "class-transformer";
import {transportMethod} from "../transport-method";

interface PipelineCategoryStatistics extends Statistics {
  // pipeline category stats here
}

@Exclude()
export class PipelineCategoryStatisticsWrapper extends StatisticsWrapper implements PipelineCategoryStatistics {
  category: TransportCategory | undefined

  constructor(category: TransportCategory | undefined) {
    super();

    this.category = category
  }

  protected getCategoryStats(category: TransportCategory): StatisticsWrapper | undefined {
    if (this.category !== category) {
      return undefined
    }

    return this
  }

  protected getCategoryStatsList(category: TransportCategory | undefined = undefined): StatisticsWrapper[] {
    const categoryStatsList: StatisticsWrapper[] = []

    if (category) {
      const categoryStats = this.getCategoryStats(category)
      if (categoryStats) {
        categoryStatsList.push(categoryStats)
      }
    } else {
      categoryStatsList.push(this)
    }

    return categoryStatsList
  }

  getValue(type: StatisticsType, rateModel: string | undefined = undefined): number {
    let value = 0;

    if (!type.method || type.method === transportMethod.pipeline) {
      if (type.cost && rateModel) {
        const cost = this.costs[rateModel];
        switch (type) {
          case statisticsType.cost:
            value = this.getTotalCost(rateModel)
            break;
          case statisticsType.billableTime:
            value = this.getBillableHours(rateModel)
            break;
          case statisticsType.billableQuantity:
            value = this.getBillableQty(rateModel)
            break;
          case statisticsType.timeCost:
            value = this.getTimeCost(rateModel)
            break;
          case statisticsType.genericTimeCost:
            value = this.getGenericTimeCost(rateModel)
            break;
          case statisticsType.siteRestrictionTimeCost:
            value = this.getSiteRestrictionTimeCost(rateModel)
            break;
          case statisticsType.demurrageTimeCost:
            value = this.getDemurrageTimeCost(rateModel)
            break;
          case statisticsType.pickupDemurrageTimeCost:
            value = this.getPickupDemurrageTimeCost(rateModel)
            break;
          case statisticsType.deliveryDemurrageTimeCost:
            value = this.getDeliveryDemurrageTimeCost(rateModel)
            break;
          case statisticsType.freightCost:
            value = this.getFreightCost(rateModel)
            break;
          case statisticsType.genericFreightCost:
            value = this.getGenericFreightCost(rateModel)
            break;
          case statisticsType.siteRestrictionFreightCost:
            value = this.getSiteRestrictionFreightCost(rateModel)
            break;
          case statisticsType.loadCost:
            value = this.getLoadCost(rateModel)
            break;
          case statisticsType.genericLoadCost:
            value = this.getGenericLoadCost(rateModel)
            break;
          case statisticsType.siteRestrictionLoadCost:
            value = this.getSiteRestrictionLoadCost(rateModel)
            break;
          case statisticsType.baseFuelSurchargeLoadCost:
            value = this.getBaseFuelSurchargeLoadCost(rateModel)
            break;
          case statisticsType.percentFuelSurchargeLoadCost:
            value = this.getPercentFuelSurchargeLoadCost(rateModel)
            break;
          case statisticsType.shiftLoadCost:
            value = this.getShiftLoadCost(rateModel)
            break;
          case statisticsType.ticketRejectLoadCost:
            value = this.getTicketRejectLoadCost(rateModel)
            break;
          case statisticsType.splitLoadCost:
            value = this.getSplitLoadCost(rateModel)
            break;
          case statisticsType.taxCost:
            value = this.getTaxCost(rateModel)
            break;
          case statisticsType.siteRestrictionCost:
            value = this.getSiteRestrictionCost(rateModel)
            break;
          case statisticsType.fuelSurchargeCost:
            value = this.getFuelSurchargeCost(rateModel)
            break;
          case statisticsType.costPerQuantity:
            value = cost.getCostPerQty(this.totalQty);
            break;
          default:
            break;
        }
      }

      switch (type) {
        case statisticsType.quantity:
          value = this.totalQty;
          break;
        case statisticsType.standardQuantity:
          value = this.totalStandardQty
          break;
        case statisticsType.netQuantity:
          value = this.totalNetQty;
          break;
        default:
          break;
      }
    }
    return value;
  }
}

export interface PipelineStatistics {
  water: PipelineCategoryStatistics
  oil: PipelineCategoryStatistics
  other: PipelineCategoryStatistics
}

@Exclude()
export class PipelineStatisticsWrapper extends StatisticsWrapper implements PipelineStatistics {

  @Expose()
  water = new PipelineCategoryStatisticsWrapper(transportCategory.water)

  @Expose()
  oil = new PipelineCategoryStatisticsWrapper(transportCategory.oil)

  @Expose()
  other = new PipelineCategoryStatisticsWrapper(undefined)

  protected getCategoryStats(category: TransportCategory): StatisticsWrapper | undefined {
    let truckingCategoryStats: StatisticsWrapper | undefined = undefined

    if (category === transportCategory.water) {
      truckingCategoryStats = this.water
    } else if (category === transportCategory.oil) {
      truckingCategoryStats = this.oil
    }

    return truckingCategoryStats
  }

  protected getCategoryStatsList(category: TransportCategory | undefined = undefined): StatisticsWrapper[] {
    const categoryStatsList: StatisticsWrapper[] = []

    if (category) {
      const categoryStats = this.getCategoryStats(category)
      if (categoryStats) {
        categoryStatsList.push(categoryStats)
      }
    } else {
      categoryStatsList.push(this.water, this.oil, this.other)
    }

    return categoryStatsList
  }

  getTotalQty(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: PipelineCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as PipelineCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalQty)
  }

  getTotalStandardQty(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: PipelineCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as PipelineCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalStandardQty)
  }

  getTotalNetQty(category: TransportCategory | undefined = undefined): number {
    const categoryStatsList: PipelineCategoryStatisticsWrapper[] = this.getCategoryStatsList(category) as PipelineCategoryStatisticsWrapper[]
    return this.sum(categoryStatsList, (stats) => stats.totalNetQty)
  }

  getCostPerQty(rateModel: string, category: TransportCategory | undefined = undefined): number {
    const totalCost = this.getTotalCost(rateModel, category)
    return calculateAverage(totalCost, this.getTotalQty(category))
  }

  getValue(type: StatisticsType, rateModel: string | undefined = undefined, category: TransportCategory | undefined = undefined): number {
    let value = 0;

    if (!type.method || type.method === transportMethod.pipeline) {
      if (type.cost && rateModel) {
        switch (type) {
          case statisticsType.cost:
            value = this.getTotalCost(rateModel, category)
            break;
          case statisticsType.billableTime:
            value = this.getBillableHours(rateModel, category)
            break;
          case statisticsType.billableQuantity:
            value = this.getBillableQty(rateModel, category)
            break;
          case statisticsType.timeCost:
            value = this.getTimeCost(rateModel, category)
            break;
          case statisticsType.genericTimeCost:
            value = this.getGenericTimeCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionTimeCost:
            value = this.getSiteRestrictionTimeCost(rateModel, category)
            break;
          case statisticsType.demurrageTimeCost:
            value = this.getDemurrageTimeCost(rateModel, category)
            break;
          case statisticsType.pickupDemurrageTimeCost:
            value = this.getPickupDemurrageTimeCost(rateModel, category)
            break;
          case statisticsType.deliveryDemurrageTimeCost:
            value = this.getDeliveryDemurrageTimeCost(rateModel, category)
            break;
          case statisticsType.freightCost:
            value = this.getFreightCost(rateModel, category)
            break;
          case statisticsType.genericFreightCost:
            value = this.getGenericFreightCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionFreightCost:
            value = this.getSiteRestrictionFreightCost(rateModel, category)
            break;
          case statisticsType.loadCost:
            value = this.getLoadCost(rateModel, category)
            break;
          case statisticsType.genericLoadCost:
            value = this.getGenericLoadCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionLoadCost:
            value = this.getSiteRestrictionLoadCost(rateModel, category)
            break;
          case statisticsType.baseFuelSurchargeLoadCost:
            value = this.getBaseFuelSurchargeLoadCost(rateModel, category)
            break;
          case statisticsType.percentFuelSurchargeLoadCost:
            value = this.getPercentFuelSurchargeLoadCost(rateModel, category)
            break;
          case statisticsType.shiftLoadCost:
            value = this.getShiftLoadCost(rateModel, category)
            break;
          case statisticsType.ticketRejectLoadCost:
            value = this.getTicketRejectLoadCost(rateModel, category)
            break;
          case statisticsType.splitLoadCost:
            value = this.getSplitLoadCost(rateModel, category)
            break;
          case statisticsType.taxCost:
            value = this.getTaxCost(rateModel, category)
            break;
          case statisticsType.siteRestrictionCost:
            value = this.getSiteRestrictionCost(rateModel, category)
            break;
          case statisticsType.fuelSurchargeCost:
            value = this.getFuelSurchargeCost(rateModel, category)
            break;
          case statisticsType.costPerQuantity:
            value = this.getCostPerQty(rateModel, category)
            break;
          default:
            break;
        }
      }

      switch (type) {
        case statisticsType.quantity:
          value = this.getTotalQty(category)
          break;
        case statisticsType.standardQuantity:
          value = this.getTotalStandardQty(category)
          break;
        case statisticsType.netQuantity:
          value = this.getTotalNetQty(category)
          break;
        default:
          break;
      }
    }
    return value;
  }
}
